import { useCallback, useState } from 'react'
import { useMutation, useQuery, UseQueryArgs } from 'urql'
import { resendRegistrationEmailMutation, sendScheduleChangeEmailMutation, sendScheduleEmailMutation, updateMembershipStatusMutation } from '../../_lib/graphql/mutations'
import { readOneUserQuery, readSeasonsQuery } from '../../_lib/graphql/queries'
import { EditUser } from '../EditUser'
import { useSnackbarPush } from '../SnackbarContext/SnackbarContext'
import { UserLogs } from '../UserLogs'
import { ProfileView } from './ProfileView'

type PropTypes = {
  userID?: string | null
}

const ProfileViewProvider = ({ userID }: PropTypes) => {
  const { pushSuccess, pushError } = useSnackbarPush()
  const [editUserOpen, setEditUserOpen] = useState(false)
  const [logsOpen, setLogsOpen] = useState(false)
  const [userResult] = useQuery({
    query: readOneUserQuery,
    variables: {
      input: {
        userID: userID || ''
      },
    },
    pause: !userID
  } as UseQueryArgs)

  const [seasonsResult] = useQuery({
    query: readSeasonsQuery
  } as UseQueryArgs)

  const [, executeUpdateMembershipStatus] = useMutation(updateMembershipStatusMutation)
  const [sendScheduleChangeEmailResult, executeSendScheduleChangeEmail] = useMutation(sendScheduleChangeEmailMutation)
  const [sendScheduleEmailResult, executeSendScheduleEmail] = useMutation(sendScheduleEmailMutation)

  const updateMembershipStatus = useCallback((membershipID: string, status: string) => {
    executeUpdateMembershipStatus({
      input: {
        membershipID,
        status
      }
    }).then(res => {
      if (res.error) {
        pushError(res.error.message)
      } else {
        switch (status) {
          case 'confirm':
            pushSuccess('Mitgliedschaft bestätigt')
            break;
          case 'reject':
            pushSuccess('Mitgliedschaft abgelehnt')
            break;
          case 'leave':
            pushSuccess('Mitgliedschaft beendet')
            break;
          case 'requested':
            pushSuccess('Mitgliedschaft zurückgesetzt auf "angefragt"')
          break;
        }
      }
    })
  }, [executeUpdateMembershipStatus, pushError, pushSuccess])

  const [, resendRegistrationEmail] = useMutation(resendRegistrationEmailMutation)

  const handleResendRegistrationEmail = useCallback(() => {
    if (!userID) {
      pushError('Kein Nutzer ausgewählt')
      return
    }

    resendRegistrationEmail({
      input: {
        userID
      }
    }).then(res => {
      if (res.error) {
        pushError(res.error.message)
      } else {
        pushSuccess('Registrierungs E-Mail versendet')
      }
    })
  }, [resendRegistrationEmail, userID, pushError, pushSuccess])

  const handleSendScheduleChangeEmail = useCallback(() => {
    if (!userID) {
      pushError('Kein Nutzer ausgewählt')
      return
    }

    executeSendScheduleChangeEmail({
      input: {
        userID
      }
    }).then(res => {
      if (res.error) {
        pushError(res.error.message)
      } else if (res.data?.sendScheduleChangeEmail) {
        pushSuccess('Schichtplan-Änderungs-E-Mail versendet')
      } else {
        pushError('Schichtplan-Änderungs-E-Mail konnte nicht versendet werden')
      }
    })
  }, [executeSendScheduleChangeEmail, pushError, pushSuccess, userID])

  const handleSendScheduleEmail = useCallback(() => {
    if (!userID) {
      pushError('Kein Nutzer ausgewählt')
      return
    }

    executeSendScheduleEmail({
      input: {
        userID
      }
    }).then(res => {
      if (res.error) {
        pushError(res.error.message)
      } else if (res.data?.sendScheduleEmail) {
        pushSuccess('Schichtplan-E-Mail versendet')
      } else {
        pushError('Schichtplan-E-Mail konnte nicht versendet werden')
      }
    })
  }, [executeSendScheduleEmail, pushError, pushSuccess, userID])

  return (
    <>
      <EditUser
        hideNotificationSettings
        userID={userID || ''}
        open={editUserOpen}
        onClose={() => setEditUserOpen(false)}
      />

      <UserLogs
        userID={userID || ''}
        open={logsOpen}
        onClose={() => setLogsOpen(false)}
      />

      <ProfileView
        editProfile={() => setEditUserOpen(true)}
        openHistory={() => setLogsOpen(true)}
        profile={userResult.data?.readOneUser}
        seasons={seasonsResult.data?.readSeasons || []}
        fetching={userResult.fetching }
        stale={userResult.stale}
        updateMembershipStatus={updateMembershipStatus}
        resendRegistrationEmail={handleResendRegistrationEmail}
        sendScheduleChangeEmail={handleSendScheduleChangeEmail}
        sendingScheduleChangeEmail={sendScheduleChangeEmailResult.fetching}
        sendScheduleEmail={handleSendScheduleEmail}
        sendingScheduleEmail={sendScheduleEmailResult.fetching}
      />
    </>
  )
}

export default ProfileViewProvider;
